/**
 * Every icon in the game, as canvas paths.
 *
 * Drawings, not the words that used to be printed on tiles — `stove · pan`,
 * `fire ext.`, `c.carrot`. The reference screenshot has no text anywhere
 * inside the kitchen and neither do we: a crate is
 * drawn as the ingredient sitting in it, a stove is a burner, a pan is a pan.
 *
 * Three rules hold everything here together.
 *
 *  - **Shape carries the meaning, colour reinforces it.** The pass is a window
 *    with a sill and the plate return is a chute — two objects, not one object
 *    mirrored, because an arrow is the last thing anybody reads. Neither is
 *    warm, because the glow the hatch used to have is what made it read as the
 *    oven. Raw and cooked differ by steam as well as by shade. Whole and
 *    chopped differ by one body against three pieces.
 *  - **No ingredient is named.** Every ingredient icon is a function of the
 *    `IngredientSpec` `catalog.ts` derived from the recipe DAG, so a new
 *    ingredient in `recipes.json` draws itself.
 *  - **It has to read at 32 pixels.** The smallest tile on the largest map is
 *    about that, so shapes are bold, closed and few. Detail that disappears at
 *    32 px is not drawn at all.
 *
 * Everything is pure drawing: no state, no time source except the tick that is
 * passed in, which is what lets the identical calls run in a live round and in
 * a replay.
 */

import type { Carryable, Container, Plate, StationType } from '@overcooked/shared'
import { BADGE_INK, BADGE_PATHS, badgeFallback } from './badgeShapes'
import type { Catalog, IngredientSpec } from './catalog'
import { roundRect } from './theme'

const TAU = Math.PI * 2

/**
 * Fixture accents. A lit burner, its knob marks and the oven lamp are one
 * orange, so a hot hob and a hot oven say "hot" in the same colour; the hatch
 * arrow and the rack's hazard strip are one pale brass, so the two "mind this"
 * marks a bench carries match each other.
 */
const BURNER = '#ff7a1f'
const BURNER_GLOW = '#ffb15a'
const TRIM = '#e8c98a'

/** Where a station's rack hangs its extinguisher, as fractions of the tile. */
export const RACKED_EXTINGUISHER = { x: 0.5, y: 0.46, r: 0.3 } as const

const hsl = (spec: Pick<IngredientSpec, 'hue' | 'sat' | 'light'>, dl = 0, ds = 0): string =>
  `hsl(${spec.hue}, ${Math.max(0, Math.min(100, spec.sat + ds))}%, ${Math.max(0, Math.min(100, spec.light + dl))}%)`

/** One light source, top-left, shared by every solid in the game. */
function solid(ctx: CanvasRenderingContext2D, fill: string, stroke: string, lw: number): void {
  ctx.fillStyle = fill
  ctx.fill()
  ctx.lineWidth = lw
  ctx.strokeStyle = stroke
  ctx.stroke()
}

// ---------------------------------------------------------------------------
// Ingredients
// ---------------------------------------------------------------------------

/**
 * One ingredient, whatever it is.
 *
 * `r` is the half-width of the box the icon fills. Everything is drawn around
 * `(cx, cy)` so the caller never has to know how tall a given shape is.
 */
export function drawIngredient(
  ctx: CanvasRenderingContext2D,
  spec: IngredientSpec,
  cx: number,
  cy: number,
  r: number,
  atTick = 0,
): void {
  const body = hsl(spec)
  const line = hsl(spec, -26, 6)
  const lw = Math.max(1, r * 0.13)

  ctx.save()
  if (spec.cut) drawCutForm(ctx, spec, cx, cy, r, body, line, lw)
  else drawWholeForm(ctx, spec, cx, cy, r, body, line, lw)

  // Cooked things steam. This is the second channel for "this is not raw" —
  // a colourblind player reads the wisps, not the shade.
  if (spec.cooked) drawSteam(ctx, cx, cy - r * 0.95, r * 0.7, atTick)
  ctx.restore()
}

function drawWholeForm(
  ctx: CanvasRenderingContext2D,
  spec: IngredientSpec,
  cx: number,
  cy: number,
  r: number,
  body: string,
  line: string,
  lw: number,
): void {
  switch (spec.shape) {
    case 'root': {
      // A tapered cone, point down, with a leafy crown: unmistakably a root
      // vegetable in silhouette alone.
      ctx.beginPath()
      ctx.moveTo(cx, cy + r)
      ctx.quadraticCurveTo(cx - r * 0.62, cy - r * 0.1, cx - r * 0.42, cy - r * 0.5)
      ctx.quadraticCurveTo(cx, cy - r * 0.78, cx + r * 0.42, cy - r * 0.5)
      ctx.quadraticCurveTo(cx + r * 0.62, cy - r * 0.1, cx, cy + r)
      ctx.closePath()
      solid(ctx, body, line, lw)
      // Three ribs, the mark every root vegetable has.
      ctx.strokeStyle = hsl(spec, -14)
      ctx.lineWidth = lw * 0.7
      for (const t of [-0.35, 0, 0.35]) {
        ctx.beginPath()
        ctx.moveTo(cx + r * t * 0.6, cy - r * 0.3)
        ctx.lineTo(cx + r * t * 0.25, cy + r * 0.45)
        ctx.stroke()
      }
      drawCrown(ctx, cx, cy - r * 0.62, r * 0.5)
      break
    }
    case 'bulb': {
      // A flattened sphere with a pointed tip and papery vertical seams.
      ctx.beginPath()
      ctx.ellipse(cx, cy + r * 0.08, r * 0.82, r * 0.76, 0, 0, TAU)
      ctx.closePath()
      solid(ctx, body, line, lw)
      ctx.strokeStyle = hsl(spec, -18)
      ctx.lineWidth = lw * 0.7
      for (const t of [-0.42, 0, 0.42]) {
        ctx.beginPath()
        ctx.moveTo(cx + r * t, cy - r * 0.62)
        ctx.quadraticCurveTo(cx + r * t * 1.5, cy + r * 0.1, cx + r * t * 0.7, cy + r * 0.76)
        ctx.stroke()
      }
      ctx.beginPath()
      ctx.moveTo(cx - r * 0.16, cy - r * 0.64)
      ctx.lineTo(cx, cy - r * 1.05)
      ctx.lineTo(cx + r * 0.16, cy - r * 0.64)
      ctx.closePath()
      solid(ctx, hsl(spec, -8, -14), line, lw * 0.8)
      break
    }
    case 'cap': {
      // A dome on a stalk.
      ctx.beginPath()
      ctx.moveTo(cx - r * 0.28, cy + r * 0.82)
      ctx.lineTo(cx - r * 0.24, cy + r * 0.02)
      ctx.lineTo(cx + r * 0.24, cy + r * 0.02)
      ctx.lineTo(cx + r * 0.28, cy + r * 0.82)
      ctx.closePath()
      solid(ctx, hsl(spec, 16, -10), line, lw * 0.8)
      ctx.beginPath()
      ctx.moveTo(cx - r * 0.92, cy + r * 0.06)
      ctx.quadraticCurveTo(cx - r * 0.86, cy - r * 0.92, cx, cy - r * 0.92)
      ctx.quadraticCurveTo(cx + r * 0.86, cy - r * 0.92, cx + r * 0.92, cy + r * 0.06)
      ctx.closePath()
      solid(ctx, body, line, lw)
      break
    }
    case 'slab': {
      // A cut of meat: a rounded block with marbling and a rind along the top.
      roundRect(ctx, cx - r * 0.88, cy - r * 0.66, r * 1.76, r * 1.34, r * 0.34)
      solid(ctx, body, line, lw)
      ctx.beginPath()
      ctx.moveTo(cx - r * 0.7, cy - r * 0.5)
      ctx.quadraticCurveTo(cx, cy - r * 0.86, cx + r * 0.7, cy - r * 0.5)
      ctx.lineWidth = lw * 1.5
      ctx.strokeStyle = hsl(spec, 26, -30)
      ctx.stroke()
      ctx.lineWidth = lw * 0.8
      ctx.strokeStyle = hsl(spec, 18, -16)
      for (const t of [-0.2, 0.24]) {
        ctx.beginPath()
        ctx.moveTo(cx - r * 0.5, cy + r * t)
        ctx.quadraticCurveTo(cx, cy + r * (t + 0.22), cx + r * 0.5, cy + r * t)
        ctx.stroke()
      }
      break
    }
    case 'leaf': {
      ctx.beginPath()
      ctx.moveTo(cx, cy - r)
      ctx.quadraticCurveTo(cx + r * 0.95, cy - r * 0.1, cx, cy + r)
      ctx.quadraticCurveTo(cx - r * 0.95, cy - r * 0.1, cx, cy - r)
      ctx.closePath()
      solid(ctx, body, line, lw)
      ctx.beginPath()
      ctx.moveTo(cx, cy - r * 0.8)
      ctx.lineTo(cx, cy + r * 0.8)
      ctx.lineWidth = lw * 0.8
      ctx.strokeStyle = hsl(spec, -20)
      ctx.stroke()
      break
    }
    case 'pod': {
      ctx.beginPath()
      ctx.ellipse(cx, cy, r * 0.95, r * 0.46, -0.35, 0, TAU)
      ctx.closePath()
      solid(ctx, body, line, lw)
      ctx.fillStyle = hsl(spec, -22)
      for (const t of [-0.45, 0, 0.45]) {
        ctx.beginPath()
        ctx.arc(cx + r * t * 0.9, cy + r * t * 0.35, r * 0.12, 0, TAU)
        ctx.fill()
      }
      break
    }
    case 'grain': {
      ctx.fillStyle = body
      ctx.strokeStyle = line
      ctx.lineWidth = lw * 0.6
      for (const [dx, dy] of [
        [-0.45, 0.3],
        [0.1, 0.45],
        [0.5, 0.05],
        [-0.15, -0.1],
        [-0.5, -0.45],
        [0.25, -0.5],
      ]) {
        ctx.beginPath()
        ctx.ellipse(cx + r * dx, cy + r * dy, r * 0.28, r * 0.15, dx + dy, 0, TAU)
        ctx.fill()
        ctx.stroke()
      }
      break
    }
    case 'sphere':
    default: {
      // A round fruit with a highlight and a small crown. Also the fallback,
      // so a shape this switch has never heard of is still a closed body.
      ctx.beginPath()
      ctx.arc(cx, cy + r * 0.06, r * 0.86, 0, TAU)
      ctx.closePath()
      solid(ctx, body, line, lw)
      ctx.beginPath()
      ctx.arc(cx - r * 0.3, cy - r * 0.3, r * 0.22, 0, TAU)
      ctx.fillStyle = hsl(spec, 18)
      ctx.fill()
      drawCrown(ctx, cx, cy - r * 0.74, r * 0.34)
      break
    }
  }
}

/**
 * The chopped form: the same family, in pieces, on an implied board line.
 *
 * Keeping the family is the whole point — three wedges read as a chopped root,
 * three rings read as a chopped bulb, and neither reads as the other, which is
 * the "a chopped carrot must not read as a chopped onion" requirement.
 */
function drawCutForm(
  ctx: CanvasRenderingContext2D,
  spec: IngredientSpec,
  cx: number,
  cy: number,
  r: number,
  body: string,
  line: string,
  lw: number,
): void {
  const at: Array<[number, number]> = [
    [-0.5, 0.28],
    [0.5, 0.28],
    [0, -0.36],
  ]
  const p = r * 0.46

  for (const [dx, dy] of at) {
    const px = cx + r * dx
    const py = cy + r * dy
    switch (spec.shape) {
      case 'root':
        // Wedges: a cone cut across is a triangle with a rounded back.
        ctx.beginPath()
        ctx.moveTo(px - p, py + p * 0.7)
        ctx.lineTo(px + p, py + p * 0.7)
        ctx.quadraticCurveTo(px, py - p * 1.1, px - p, py + p * 0.7)
        ctx.closePath()
        solid(ctx, body, line, lw * 0.85)
        break
      case 'bulb':
        // Rings, drawn as a ring rather than a disc so the hole reads.
        ctx.beginPath()
        ctx.arc(px, py, p, 0, TAU)
        ctx.closePath()
        solid(ctx, body, line, lw * 0.85)
        ctx.beginPath()
        ctx.arc(px, py, p * 0.5, 0, TAU)
        ctx.lineWidth = lw * 0.85
        ctx.strokeStyle = line
        ctx.stroke()
        break
      case 'cap':
        // A mushroom cut lengthways is a little T.
        ctx.beginPath()
        ctx.moveTo(px - p, py + p * 0.1)
        ctx.quadraticCurveTo(px, py - p * 1.15, px + p, py + p * 0.1)
        ctx.lineTo(px + p * 0.32, py + p * 0.1)
        ctx.lineTo(px + p * 0.28, py + p * 0.95)
        ctx.lineTo(px - p * 0.28, py + p * 0.95)
        ctx.lineTo(px - p * 0.32, py + p * 0.1)
        ctx.closePath()
        solid(ctx, body, line, lw * 0.85)
        break
      case 'slab':
        roundRect(ctx, px - p, py - p * 0.78, p * 2, p * 1.56, p * 0.3)
        solid(ctx, body, line, lw * 0.85)
        break
      case 'leaf':
        // Shreds.
        roundRect(ctx, px - p, py - p * 0.3, p * 2, p * 0.6, p * 0.3)
        solid(ctx, body, line, lw * 0.7)
        break
      case 'pod':
        ctx.beginPath()
        ctx.ellipse(px, py, p, p * 0.55, 0.4, 0, TAU)
        ctx.closePath()
        solid(ctx, body, line, lw * 0.8)
        break
      case 'grain':
        ctx.beginPath()
        ctx.ellipse(px, py, p * 0.75, p * 0.4, dx, 0, TAU)
        ctx.closePath()
        solid(ctx, body, line, lw * 0.6)
        break
      case 'sphere':
      default:
        // A sphere cut in half.
        ctx.beginPath()
        ctx.arc(px, py + p * 0.4, p, Math.PI, TAU)
        ctx.closePath()
        solid(ctx, body, line, lw * 0.85)
        break
    }
  }
}

/** The green top a root or a fruit carries. Small, but it sells the shape. */
function drawCrown(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number): void {
  ctx.fillStyle = '#4e8f3d'
  for (const t of [-0.7, 0, 0.7]) {
    ctx.beginPath()
    ctx.moveTo(cx, cy + r * 0.3)
    ctx.quadraticCurveTo(cx + r * t * 1.4, cy - r * 0.7, cx + r * t * 0.6, cy - r * 1.1)
    ctx.quadraticCurveTo(cx + r * t * 0.2, cy - r * 0.3, cx, cy + r * 0.3)
    ctx.closePath()
    ctx.fill()
  }
}

/**
 * Steam. Two wisps that drift and fade on the tick, so "hot" is motion as well
 * as colour — the cue survives a monochrome screenshot.
 */
function drawSteam(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  atTick: number,
): void {
  ctx.save()
  ctx.lineCap = 'round'
  ctx.lineWidth = Math.max(1, r * 0.22)
  for (let i = 0; i < 2; i += 1) {
    const phase = (atTick * 0.02 + i * 0.5) % 1
    const lift = phase * r * 1.2
    ctx.globalAlpha = 0.55 * (1 - phase)
    ctx.strokeStyle = '#ffffff'
    ctx.beginPath()
    const x = cx + (i === 0 ? -r * 0.42 : r * 0.42)
    ctx.moveTo(x, cy - lift)
    ctx.quadraticCurveTo(x + r * 0.35, cy - lift - r * 0.35, x, cy - lift - r * 0.7)
    ctx.stroke()
  }
  ctx.restore()
}

// ---------------------------------------------------------------------------
// Containers — the phase-7 open question, answered by silhouette
// ---------------------------------------------------------------------------

/**
 * The pan.
 *
 * There used to be two vessels here and half this file's length was spent
 * keeping them apart in outline — pan shallow with one long handle, pot deep
 * with two lugs and a rim. Content now ships one kind, so the fork is gone and
 * every container is drawn as a skillet: shallow body, sloped sides, ONE long
 * handle out to the right, identifiable in a black-and-white printout of a
 * 32-pixel tile.
 *
 * A kind the content introduces later gets this silhouette too. That is a
 * deliberate choice over a generic fallback shape: an unfamiliar vessel drawn
 * as a familiar one is legible and slightly wrong, whereas a grey box is
 * neither, and the day a second kind ships is the day it earns its own outline.
 */
export function drawContainer(
  ctx: CanvasRenderingContext2D,
  container: Container,
  cx: number,
  cy: number,
  r: number,
  catalog: Catalog,
  atTick = 0,
): void {
  const burnt = container.cookState === 'burnt'
  // Round 1's vessels were charcoal on charcoal and read as blobs: the eye had
  // nothing to catch. The metal is lighter now, with a specular down one side,
  // so the vessel has volume — and, more to the point, so the food inside it is
  // the brightest thing on the tile rather than the darkest.
  const metal = burnt ? '#2f2621' : '#5c6672'
  const metalLight = burnt ? '#453931' : '#8c98a6'
  const lw = Math.max(1, r * 0.12)

  ctx.save()
  // Handle first, so the body's stroke sits on top of it.
  ctx.beginPath()
  roundRect(ctx, cx + r * 0.58, cy - r * 0.14, r * 1.14, r * 0.26, r * 0.13)
  solid(ctx, '#2f261f', '#181310', lw * 0.8)

  // A skillet is *shallow*. Making the body squat is what stops it reading as a
  // bucket at 100 pixels, and with the pot gone it is the whole silhouette.
  ctx.beginPath()
  ctx.moveTo(cx - r * 0.92, cy - r * 0.26)
  ctx.lineTo(cx + r * 0.72, cy - r * 0.26)
  ctx.lineTo(cx + r * 0.56, cy + r * 0.18)
  ctx.quadraticCurveTo(cx - r * 0.1, cy + r * 0.42, cx - r * 0.78, cy + r * 0.18)
  ctx.closePath()
  solid(ctx, metal, '#20262e', lw)

  ctx.beginPath()
  ctx.ellipse(cx - r * 0.1, cy - r * 0.26, r * 0.84, r * 0.22, 0, 0, TAU)
  solid(ctx, metalLight, '#20262e', lw * 0.8)

  drawContainerContents(ctx, container, cx, cy, r, catalog, atTick)
  ctx.restore()
}

function drawContainerContents(
  ctx: CanvasRenderingContext2D,
  container: Container,
  cx: number,
  cy: number,
  r: number,
  catalog: Catalog,
  atTick: number,
): void {
  if (container.contents.length === 0) return

  // The bowl of the pan, in the body's own coordinates. Everything below is
  // placed against these two numbers rather than against `cy`, so a change to
  // the silhouette carries the food with it.
  const bowlX = cx - r * 0.1
  const bowlY = cy - r * 0.3

  if (container.cookState === 'burnt') {
    // Char: a black crust with a broken surface, so burnt reads even in
    // greyscale and even before the label is looked at.
    ctx.beginPath()
    ctx.ellipse(bowlX, cy - r * 0.28, r * 0.66, r * 0.2, 0, 0, TAU)
    ctx.fillStyle = '#1b1512'
    ctx.fill()
    ctx.strokeStyle = '#4a3a30'
    ctx.lineWidth = Math.max(1, r * 0.08)
    for (const t of [-0.3, 0.3]) {
      ctx.beginPath()
      ctx.moveTo(cx + r * t, cy - r * 0.4)
      ctx.lineTo(cx + r * t * 0.4, cy - r * 0.16)
      ctx.stroke()
    }
    drawSmoke(ctx, cx, cy - r * 0.7, r * 0.6, atTick)
    return
  }

  // What is in the vessel is the thing a player is actually tracking, so it is
  // drawn as a surface of food — a broth in the ingredients' own colours, sat
  // inside the rim — with the pieces proud of it. Round 1 drew three small
  // icons half-hidden behind the rim, and from across the kitchen a full vessel
  // and an empty one looked the same.
  //
  // The shipped pan holds one, so in a live round this draws a single piece —
  // but the sizing still scales to three, because a plate mid-assembly is drawn
  // by the same code and content is free to ship a larger vessel. The pieces
  // shrink and the spread widens together, so a full vessel reads as distinct
  // things rather than one crowded lump.
  const n = container.contents.length
  const specs = container.contents.map((id) => catalog.ingredient(id))

  // The food surface deliberately uses the ingredient's own bright hue rather
  // than the darker cooked-piece colour. It is part of this container drawing,
  // not a stove decoration, so the marker follows a pan onto a counter or into
  // a chef's hands and keeps meat and carrot immediately distinguishable.
  ctx.save()
  ctx.beginPath()
  ctx.ellipse(bowlX, bowlY, r * 0.78, r * 0.2, 0, 0, TAU)
  ctx.fillStyle = hsl(specs[0], 16, 18)
  ctx.fill()
  ctx.lineWidth = Math.max(1, r * 0.07)
  ctx.strokeStyle = hsl(specs[0], -28, 8)
  ctx.stroke()
  ctx.restore()

  const spread = r * (n > 2 ? 0.5 : 0.4)
  specs.forEach((spec, i) => {
    const offset = n === 1 ? 0 : (i / (n - 1) - 0.5) * 2 * spread
    drawIngredient(ctx, spec, bowlX + offset, bowlY - r * 0.14, r * (n > 2 ? 0.26 : 0.34), atTick)
  })
}

/** Black smoke: burnt, and about to be worse. */
function drawSmoke(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  atTick: number,
): void {
  ctx.save()
  for (let i = 0; i < 3; i += 1) {
    const phase = (atTick * 0.015 + i * 0.33) % 1
    ctx.globalAlpha = 0.5 * (1 - phase)
    ctx.fillStyle = '#3a3330'
    ctx.beginPath()
    ctx.arc(cx + Math.sin(phase * 6 + i) * r * 0.4, cy - phase * r * 1.6, r * (0.22 + phase * 0.3), 0, TAU)
    ctx.fill()
  }
  ctx.restore()
}

// ---------------------------------------------------------------------------
// Plates
// ---------------------------------------------------------------------------

export function drawPlate(
  ctx: CanvasRenderingContext2D,
  plate: Plate,
  cx: number,
  cy: number,
  r: number,
  catalog: Catalog,
  atTick = 0,
): void {
  const lw = Math.max(1, r * 0.1)
  ctx.save()
  ctx.beginPath()
  ctx.ellipse(cx, cy, r, r * 0.82, 0, 0, TAU)
  ctx.closePath()
  solid(ctx, plate.clean ? '#fbf7ef' : '#8d8a63', plate.clean ? '#b9bec7' : '#5a5740', lw)

  ctx.beginPath()
  ctx.ellipse(cx, cy, r * 0.68, r * 0.55, 0, 0, TAU)
  ctx.lineWidth = lw * 0.8
  ctx.strokeStyle = plate.clean ? '#d7dde5' : '#6d6a4d'
  ctx.stroke()

  if (!plate.clean) {
    // Smears: dirty is not merely a duller white. Three arcs of residue, so the
    // state survives a colourblind reading and a greyscale screenshot.
    ctx.strokeStyle = '#5f5c3f'
    ctx.lineWidth = lw * 1.1
    ctx.lineCap = 'round'
    for (const t of [-0.3, 0.05, 0.4]) {
      ctx.beginPath()
      ctx.arc(cx + r * t * 0.5, cy + r * t * 0.3, r * 0.3, 0.6, 2.4)
      ctx.stroke()
    }
  }

  if (plate.contents.length > 0) {
    const n = plate.contents.length
    plate.contents.forEach((id, i) => {
      const angle = -Math.PI / 2 + (i / n) * TAU
      const rr = n === 1 ? 0 : r * 0.34
      drawIngredient(
        ctx,
        catalog.ingredient(id),
        cx + Math.cos(angle) * rr,
        cy + Math.sin(angle) * rr * 0.8,
        r * (n === 1 ? 0.52 : 0.34),
        atTick,
      )
    })
  }
  ctx.restore()
}

// ---------------------------------------------------------------------------
// Carried equipment
// ---------------------------------------------------------------------------

/**
 * The fire extinguisher.
 *
 * Round 1's version read as a first-aid kit: a rounded red box with a white
 * band across it. What makes an extinguisher an extinguisher is the *profile* —
 * a domed cylinder, a squeeze lever on top, and a hose looping off one side to
 * a horn — so all three are drawn, and the white band is now a small label with
 * a flame struck through it rather than a stripe across the whole body.
 */
export function drawExtinguisher(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
): void {
  const lw = Math.max(1, r * 0.1)
  ctx.save()

  // The hose, behind the body, looping out to the right and down.
  ctx.beginPath()
  ctx.moveTo(cx + r * 0.14, cy - r * 0.7)
  ctx.quadraticCurveTo(cx + r * 0.92, cy - r * 0.62, cx + r * 0.72, cy + r * 0.1)
  ctx.lineWidth = lw * 1.3
  ctx.lineCap = 'round'
  ctx.strokeStyle = '#241f1c'
  ctx.stroke()
  // The horn on the end of it.
  ctx.beginPath()
  ctx.moveTo(cx + r * 0.56, cy + r * 0.02)
  ctx.lineTo(cx + r * 0.94, cy + r * 0.24)
  ctx.lineTo(cx + r * 0.6, cy + r * 0.38)
  ctx.closePath()
  solid(ctx, '#2f2a26', '#151211', lw * 0.7)

  // The body: domed shoulder, straight sides, a foot ring.
  ctx.beginPath()
  ctx.moveTo(cx - r * 0.34, cy - r * 0.34)
  ctx.quadraticCurveTo(cx - r * 0.34, cy - r * 0.62, cx, cy - r * 0.64)
  ctx.quadraticCurveTo(cx + r * 0.34, cy - r * 0.62, cx + r * 0.34, cy - r * 0.34)
  ctx.lineTo(cx + r * 0.38, cy + r * 0.62)
  ctx.quadraticCurveTo(cx, cy + r * 0.76, cx - r * 0.38, cy + r * 0.62)
  ctx.closePath()
  solid(ctx, '#cf3325', '#6d190f', lw)
  // The specular down the left, which is what makes it a cylinder.
  ctx.beginPath()
  roundRect(ctx, cx - r * 0.24, cy - r * 0.42, r * 0.13, r * 0.86, r * 0.065)
  ctx.fillStyle = 'rgba(255, 200, 180, 0.45)'
  ctx.fill()

  // Neck and squeeze lever.
  ctx.beginPath()
  roundRect(ctx, cx - r * 0.13, cy - r * 0.84, r * 0.26, r * 0.24, r * 0.05)
  solid(ctx, '#38312c', '#151211', lw * 0.6)
  ctx.beginPath()
  roundRect(ctx, cx - r * 0.3, cy - r * 0.96, r * 0.56, r * 0.13, r * 0.06)
  solid(ctx, '#4b433c', '#151211', lw * 0.6)

  // The label: a pale plate with a flame on it, so the thing it is for is
  // written on the thing itself.
  ctx.beginPath()
  roundRect(ctx, cx - r * 0.24, cy - r * 0.12, r * 0.48, r * 0.42, r * 0.06)
  solid(ctx, '#f4ece0', '#8d7f6f', lw * 0.5)
  drawFlame(ctx, cx, cy + r * 0.26, r * 0.17, 0)
  ctx.restore()
}

// ---------------------------------------------------------------------------
// Fire
// ---------------------------------------------------------------------------

/**
 * A flame. Two overlapping tongues that flicker out of phase, so it moves like
 * fire rather than blinking like a warning light.
 *
 * `intensity` is how much fire is left: 1 is untouched, 0 is out. It scales the
 * tongues rather than fading them, because a flame that shrinks reads as "this
 * is working" and a flame that goes transparent reads as a rendering glitch.
 * The floor of 0.18 is deliberate — the tile is still on fire until the engine
 * says otherwise, and a flame that vanished before `FIRE_EXTINGUISHED` landed
 * would have the player walk away one tick early.
 */
export function drawFlame(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  atTick: number,
  intensity = 1,
): void {
  const alive = Math.max(0.18, Math.min(1, intensity))
  ctx.save()
  const tongues: Array<[number, string, number]> = [
    [1, '#ff7a1f', 0],
    [0.62, '#ffd24a', 1.7],
  ]
  for (const [baseScale, colour, offset] of tongues) {
    const scale = baseScale * alive
    const wobble = Math.sin(atTick * 0.28 + offset) * r * 0.12
    const height = r * scale * (1.5 + Math.sin(atTick * 0.21 + offset) * 0.12)
    ctx.beginPath()
    ctx.moveTo(cx - r * 0.62 * scale, cy)
    ctx.quadraticCurveTo(cx - r * 0.5 * scale, cy - height * 0.55, cx + wobble * 0.4, cy - height)
    ctx.quadraticCurveTo(cx + r * 0.62 * scale, cy - height * 0.5, cx + r * 0.62 * scale, cy)
    ctx.quadraticCurveTo(cx, cy + r * 0.2 * scale, cx - r * 0.62 * scale, cy)
    ctx.closePath()
    ctx.fillStyle = colour
    ctx.fill()
  }
  ctx.restore()
}

/**
 * The extinguisher, doing something.
 *
 * A cone of foam arriving at `(cx, cy)` from `towards + π` — the angle is the
 * direction the spray *travels*, so the caller passes "from the chef to the
 * tile" and the nozzle end works itself out. Particles are placed from a cheap
 * deterministic hash of their index and the tick rather than `Math.random`, so
 * two clients watching the same replay see the same spray, and so a paused
 * replay does not shimmer.
 *
 * This is the answer to "I hold space and nothing happens". Everything else
 * about the hold was already correct and already invisible.
 */
export function drawExtinguisherSpray(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  atTick: number,
  towards: number,
): void {
  // The nozzle sits back along the incoming angle, just off the tile.
  const nx = cx - Math.cos(towards) * r * 1.15
  const ny = cy - Math.sin(towards) * r * 1.15
  const spread = 0.42

  ctx.save()
  ctx.globalCompositeOperation = 'lighter'

  // The cone itself: a soft wedge from the nozzle, brightest where it lands.
  const gradient = ctx.createLinearGradient(nx, ny, cx, cy)
  gradient.addColorStop(0, 'rgba(226, 240, 255, 0.55)')
  gradient.addColorStop(1, 'rgba(168, 208, 240, 0.06)')
  ctx.fillStyle = gradient
  ctx.beginPath()
  ctx.moveTo(nx, ny)
  ctx.arc(nx, ny, r * 1.5, towards - spread, towards + spread)
  ctx.closePath()
  ctx.fill()

  // Particles travelling down the cone. Each has its own lifetime, so the jet
  // reads as continuous rather than as one advancing wall of dots.
  for (let i = 0; i < 14; i += 1) {
    const seed = Math.abs(Math.sin(i * 12.9898) * 43758.5453) % 1
    const travel = ((atTick * 0.07 + seed) % 1)
    const angle = towards + (seed - 0.5) * spread * 2
    const reach = r * 1.5 * travel
    const px = nx + Math.cos(angle) * reach
    const py = ny + Math.sin(angle) * reach
    ctx.globalAlpha = 0.75 * (1 - travel)
    ctx.fillStyle = '#eef6ff'
    ctx.beginPath()
    ctx.arc(px, py, r * (0.06 + travel * 0.16), 0, Math.PI * 2)
    ctx.fill()
  }

  ctx.restore()
}

// ---------------------------------------------------------------------------
// Anything a player can be holding
// ---------------------------------------------------------------------------

export function drawCarryable(
  ctx: CanvasRenderingContext2D,
  item: Carryable,
  cx: number,
  cy: number,
  r: number,
  catalog: Catalog,
  atTick = 0,
): void {
  if (item.type === 'equipment') {
    drawExtinguisher(ctx, cx, cy, r)
    return
  }
  if (item.type === 'container') {
    drawContainer(ctx, item.container, cx, cy, r, catalog, atTick)
    return
  }
  if (item.type === 'plate') {
    drawPlate(ctx, item.plate, cx, cy, r, catalog, atTick)
    return
  }
  drawIngredient(ctx, catalog.ingredient(item.id), cx, cy, r, atTick)
}

// ---------------------------------------------------------------------------
// Station fixtures
//
// Drawn into a tile box. The station's *surface* is painted by `stations.ts`;
// this is the equipment standing on it.
// ---------------------------------------------------------------------------

interface IconBox {
  x: number
  y: number
  size: number
}

/** The one function `stations.ts` calls. `atTick` drives burner glow and water. */
export function drawStationFixture(
  ctx: CanvasRenderingContext2D,
  type: StationType,
  box: IconBox,
  atTick: number,
  hot: boolean,
): void {
  const { x, y, size } = box
  const cx = x + size / 2
  const s = size
  const lw = Math.max(1, s * 0.045)

  ctx.save()
  switch (type) {
    case 'board': {
      // A board with a knife across it.
      //
      // Round 1's version read as a pencil on a ruled notepad, and it was both
      // halves that were wrong: the horizontal grain lines made a jotter and
      // the blade was a sliver. So the grain is gone, the board gets the
      // profile a chopping board actually has — a handle stub with a hanging
      // hole at one end — and the knife gets a blade with real width, a
      // straight spine, a curved edge and a riveted handle.
      ctx.beginPath()
      roundRect(ctx, x + s * 0.1, y + s * 0.18, s * 0.72, s * 0.66, s * 0.09)
      solid(ctx, '#e3c095', '#8d6438', lw)
      // The handle stub, off the right end. It is the whole silhouette.
      ctx.beginPath()
      roundRect(ctx, x + s * 0.78, y + s * 0.36, s * 0.16, s * 0.3, s * 0.07)
      solid(ctx, '#e3c095', '#8d6438', lw)
      ctx.beginPath()
      ctx.arc(x + s * 0.86, y + s * 0.51, s * 0.045, 0, TAU)
      ctx.fillStyle = '#8d6438'
      ctx.fill()

      // Cut marks: short nicks, scattered, nothing that lines up into rule.
      ctx.strokeStyle = 'rgba(141, 100, 56, 0.5)'
      ctx.lineWidth = lw * 0.7
      ctx.lineCap = 'round'
      for (const [mx, my, mw] of [
        [0.22, 0.32, 0.1],
        [0.34, 0.68, 0.13],
        [0.5, 0.28, 0.09],
      ]) {
        ctx.beginPath()
        ctx.moveTo(x + s * mx, y + s * my)
        ctx.lineTo(x + s * (mx + mw), y + s * (my + 0.03))
        ctx.stroke()
      }

      // The knife. Handle bottom-left, tip top-right, spine straight and edge
      // curved — the shape everybody recognises even at 33 pixels.
      const bx = x + s * 0.3
      const by = y + s * 0.7
      const tx = x + s * 0.78
      const ty = y + s * 0.22
      ctx.beginPath()
      ctx.moveTo(bx, by)
      ctx.lineTo(tx, ty)
      ctx.quadraticCurveTo(
        x + s * 0.48,
        y + s * 0.42,
        bx - s * 0.11,
        by + s * 0.11,
      )
      ctx.closePath()
      solid(ctx, '#d6dee7', '#4e5964', lw * 0.8)
      // The bolster and the handle.
      ctx.beginPath()
      roundRect(ctx, bx - s * 0.05, by - s * 0.05, s * 0.1, s * 0.1, s * 0.02)
      solid(ctx, '#9aa5b1', '#4e5964', lw * 0.5)
      ctx.save()
      ctx.translate(bx - s * 0.03, by + s * 0.03)
      ctx.rotate(Math.PI * 0.75)
      ctx.beginPath()
      roundRect(ctx, 0, -s * 0.055, s * 0.26, s * 0.11, s * 0.045)
      solid(ctx, '#3b2a1e', '#1b120c', lw * 0.6)
      ctx.fillStyle = '#c9b08a'
      for (const t of [0.09, 0.18]) {
        ctx.beginPath()
        ctx.arc(s * t, 0, s * 0.017, 0, TAU)
        ctx.fill()
      }
      ctx.restore()
      break
    }
    case 'stove': {
      // A hob: a steel top, a burner ring that lights when something is
      // cooking, and two control knobs on the front so it reads as a cooker
      // rather than as a target.
      ctx.beginPath()
      roundRect(ctx, x + s * 0.08, y + s * 0.08, s * 0.84, s * 0.84, s * 0.1)
      solid(ctx, '#3a4048', '#1a1d22', lw)
      ctx.beginPath()
      roundRect(ctx, x + s * 0.12, y + s * 0.12, s * 0.76, s * 0.5, s * 0.07)
      solid(ctx, '#2b3037', '#171a1f', lw * 0.6)

      const ringY = y + s * 0.37
      ctx.beginPath()
      ctx.arc(cx, ringY, s * 0.25, 0, TAU)
      ctx.lineWidth = lw * 2
      ctx.strokeStyle = hot ? BURNER : '#1d2126'
      ctx.stroke()
      ctx.beginPath()
      ctx.arc(cx, ringY, s * 0.13, 0, TAU)
      ctx.lineWidth = lw * 1.4
      ctx.strokeStyle = hot ? BURNER_GLOW : '#1d2126'
      ctx.stroke()
      if (hot) {
        ctx.globalAlpha = 0.3 + 0.12 * Math.sin(atTick * 0.16)
        ctx.beginPath()
        ctx.arc(cx, ringY, s * 0.32, 0, TAU)
        ctx.fillStyle = BURNER
        ctx.fill()
        ctx.globalAlpha = 1
      }

      for (const t of [0.32, 0.68]) {
        ctx.beginPath()
        ctx.arc(x + s * t, y + s * 0.78, s * 0.075, 0, TAU)
        solid(ctx, '#9aa5b1', '#3d444d', lw * 0.6)
        ctx.beginPath()
        ctx.moveTo(x + s * t, y + s * 0.78)
        ctx.lineTo(x + s * t, y + s * 0.72)
        ctx.lineWidth = lw * 0.8
        ctx.strokeStyle = hot ? BURNER : '#3d444d'
        ctx.stroke()
      }
      break
    }
    case 'oven': {
      // A front-loading box: door, window, handle, and a status lamp. Nothing
      // like the hob in outline, which is the point of drawing it at all.
      ctx.beginPath()
      roundRect(ctx, x + s * 0.08, y + s * 0.08, s * 0.84, s * 0.84, s * 0.08)
      solid(ctx, '#2c3038', '#15181d', lw)
      ctx.beginPath()
      roundRect(ctx, x + s * 0.14, y + s * 0.3, s * 0.72, s * 0.54, s * 0.07)
      solid(ctx, '#3b414a', '#191c21', lw * 0.8)
      ctx.beginPath()
      roundRect(ctx, x + s * 0.22, y + s * 0.4, s * 0.56, s * 0.34, s * 0.05)
      solid(ctx, hot ? '#c9631d' : '#5b4a3a', '#0f1216', lw * 0.6)
      if (!hot) {
        // A cold oven window is still glass: a diagonal sheen across it, so the
        // door reads as a door and not as a second hob.
        ctx.save()
        ctx.beginPath()
        roundRect(ctx, x + s * 0.22, y + s * 0.4, s * 0.56, s * 0.34, s * 0.05)
        ctx.clip()
        ctx.fillStyle = 'rgba(255, 240, 214, 0.16)'
        ctx.beginPath()
        ctx.moveTo(x + s * 0.22, y + s * 0.74)
        ctx.lineTo(x + s * 0.52, y + s * 0.4)
        ctx.lineTo(x + s * 0.7, y + s * 0.4)
        ctx.lineTo(x + s * 0.4, y + s * 0.74)
        ctx.closePath()
        ctx.fill()
        ctx.restore()
      }
      if (hot) {
        ctx.save()
        ctx.beginPath()
        roundRect(ctx, x + s * 0.22, y + s * 0.4, s * 0.56, s * 0.34, s * 0.05)
        ctx.clip()
        ctx.globalAlpha = 0.4 + 0.15 * Math.sin(atTick * 0.12)
        ctx.fillStyle = BURNER_GLOW
        ctx.fillRect(x + s * 0.22, y + s * 0.4, s * 0.56, s * 0.34)
        ctx.restore()
      }
      // Handle across the top of the door, and the lamp beside it.
      ctx.beginPath()
      roundRect(ctx, x + s * 0.16, y + s * 0.16, s * 0.52, s * 0.09, s * 0.045)
      solid(ctx, '#96a0ad', '#4d545d', lw * 0.6)
      ctx.beginPath()
      ctx.arc(x + s * 0.79, y + s * 0.2, s * 0.055, 0, TAU)
      solid(ctx, hot ? BURNER : '#3d444d', '#15181d', lw * 0.5)
      break
    }
    case 'sink': {
      // A basin.
      //
      // Round 1's sink read as a briefcase: a shallow tray with a tall handle
      // over it. What makes a sink a sink is the *recess* — a rim you can see
      // the thickness of, and water sitting below it — so the basin now fills
      // the tile, the water is a field rather than a stripe, and the tap is a
      // small spout at the back instead of a handle across the top.
      ctx.beginPath()
      roundRect(ctx, x + s * 0.06, y + s * 0.16, s * 0.88, s * 0.74, s * 0.12)
      solid(ctx, '#b6c1cc', '#4f5a66', lw)
      // Rim highlight along the top edge, so the rim has thickness.
      ctx.beginPath()
      roundRect(ctx, x + s * 0.1, y + s * 0.19, s * 0.8, s * 0.1, s * 0.05)
      ctx.fillStyle = 'rgba(255,255,255,0.5)'
      ctx.fill()

      // The recess. Darker, with the wall shadow across its top.
      ctx.beginPath()
      roundRect(ctx, x + s * 0.16, y + s * 0.3, s * 0.68, s * 0.48, s * 0.08)
      solid(ctx, '#2f4655', '#22323d', lw * 0.7)
      ctx.save()
      ctx.beginPath()
      roundRect(ctx, x + s * 0.16, y + s * 0.3, s * 0.68, s * 0.48, s * 0.08)
      ctx.clip()
      // Water, filling most of the recess.
      ctx.fillStyle = '#3f86b8'
      ctx.fillRect(x + s * 0.16, y + s * 0.42, s * 0.68, s * 0.36)
      ctx.fillStyle = 'rgba(190, 232, 252, 0.55)'
      ctx.fillRect(x + s * 0.16, y + s * 0.42, s * 0.68, s * 0.05)
      ctx.strokeStyle = 'rgba(224, 245, 255, 0.75)'
      ctx.lineWidth = lw * 0.75
      for (let i = 0; i < 2; i += 1) {
        const yy = y + s * (0.55 + i * 0.1)
        const drift = Math.sin(atTick * 0.05 + i) * s * 0.03
        ctx.beginPath()
        ctx.moveTo(x + s * 0.22, yy)
        ctx.quadraticCurveTo(x + s * 0.36 + drift, yy - s * 0.05, x + s * 0.5, yy)
        ctx.quadraticCurveTo(x + s * 0.64 + drift, yy + s * 0.05, x + s * 0.78, yy)
        ctx.stroke()
      }
      ctx.restore()

      // The spout: short, at the back, arching over the rim into the basin.
      ctx.beginPath()
      ctx.moveTo(x + s * 0.74, y + s * 0.28)
      ctx.lineTo(x + s * 0.74, y + s * 0.14)
      ctx.quadraticCurveTo(x + s * 0.74, y + s * 0.06, x + s * 0.6, y + s * 0.08)
      ctx.lineWidth = lw * 1.6
      ctx.lineCap = 'round'
      ctx.strokeStyle = '#ccd5de'
      ctx.stroke()
      break
    }
    case 'bin': {
      ctx.beginPath()
      ctx.moveTo(x + s * 0.24, y + s * 0.3)
      ctx.lineTo(x + s * 0.76, y + s * 0.3)
      ctx.lineTo(x + s * 0.68, y + s * 0.86)
      ctx.lineTo(x + s * 0.32, y + s * 0.86)
      ctx.closePath()
      solid(ctx, '#4e555e', '#23282e', lw)
      ctx.strokeStyle = '#697280'
      ctx.lineWidth = lw * 0.8
      for (const t of [0.42, 0.55]) {
        ctx.beginPath()
        ctx.moveTo(x + s * (0.34 + (t - 0.42) * 0.1), y + s * t)
        ctx.lineTo(x + s * (0.66 - (t - 0.42) * 0.1), y + s * t)
        ctx.stroke()
      }
      ctx.beginPath()
      roundRect(ctx, x + s * 0.18, y + s * 0.2, s * 0.64, s * 0.12, s * 0.05)
      solid(ctx, '#697280', '#23282e', lw * 0.8)
      ctx.beginPath()
      roundRect(ctx, x + s * 0.44, y + s * 0.12, s * 0.12, s * 0.08, s * 0.03)
      solid(ctx, '#697280', '#23282e', lw * 0.6)
      break
    }
    case 'hatch': {
      // The pass: a window in the wall with a plate going out through it.
      //
      // Two redraws, two different complaints. The first was that a lit arch
      // with a bell read as the oven three tiles up, so the glow went and both
      // ends of the plate loop became conveyors — one running right, one
      // running left. That fixed the oven and created a worse one: a
      // playtester could not tell the pass from the plate return, because a
      // belt going one way and a belt going the other way are the same object
      // at a glance and the arrow is the last thing anybody reads. They served
      // into the return.
      //
      // So they stop being one machine drawn twice. This is an opening —
      // uprights, a lintel, a sill wide enough to slide a plate along, and no
      // heat anywhere near it. The return below is a chute. Different
      // silhouette, different half of the tile, nothing to mirror.
      ctx.beginPath()
      roundRect(ctx, x + s * 0.06, y + s * 0.08, s * 0.88, s * 0.84, s * 0.06)
      solid(ctx, '#39404a', '#171a1f', lw)

      // The opening. Dark, and running out past the right edge of the frame:
      // a plate that goes in there does not come back, which is the one fact
      // about this station a player has to hold.
      ctx.beginPath()
      roundRect(ctx, x + s * 0.14, y + s * 0.26, s * 0.8, s * 0.36, s * 0.04)
      solid(ctx, '#12161b', '#080b0e', lw * 0.6)

      // Lintel across the top and a jamb down the left, in the same steel as
      // the sill. Three sides framed and the fourth open is what makes this a
      // window rather than an oven door — the oven is a box with a handle
      // above its glass, and a handle above a dark rectangle is exactly what a
      // lintel is not allowed to look like. The open side is the side the
      // arrow points at.
      ctx.beginPath()
      roundRect(ctx, x + s * 0.06, y + s * 0.22, s * 0.88, s * 0.06, s * 0.02)
      solid(ctx, '#8d99a6', '#4a525b', lw * 0.5)
      ctx.beginPath()
      roundRect(ctx, x + s * 0.08, y + s * 0.22, s * 0.08, s * 0.46, s * 0.02)
      solid(ctx, '#8d99a6', '#4a525b', lw * 0.5)

      // A plate crossing the sill, on a loop. The belt's motion was the one
      // thing worth keeping from it — a moving tile is findable out of the
      // corner of an eye — but here the thing that moves is the cargo rather
      // than the machine, so it says "things leave here" instead of "this is a
      // conveyor".
      ctx.save()
      ctx.beginPath()
      roundRect(ctx, x + s * 0.14, y + s * 0.26, s * 0.8, s * 0.36, s * 0.04)
      ctx.clip()
      const slide = ((atTick * 0.005) % 1 + 1) % 1
      ctx.globalAlpha = Math.max(0, Math.min(1, Math.min(slide, 1 - slide) * 5))
      ctx.beginPath()
      ctx.ellipse(x + s * (0.18 + slide * 0.78), y + s * 0.46, s * 0.14, s * 0.055, 0, 0, TAU)
      solid(ctx, '#f3ede1', '#98a1ab', lw * 0.5)
      ctx.restore()

      // The sill: a lit steel ledge the full width of the tile, with its
      // shadow under it. It is the surface the plate is standing on, and the
      // reason the opening reads as a window rather than as a doorway.
      ctx.beginPath()
      roundRect(ctx, x + s * 0.02, y + s * 0.62, s * 0.96, s * 0.12, s * 0.04)
      solid(ctx, '#8d99a6', '#4a525b', lw * 0.7)
      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)'
      ctx.fillRect(x + s * 0.08, y + s * 0.74, s * 0.84, s * 0.05)

      // The direction arrow, in the header band above the opening. Colour and
      // shape both say "out", so the tile survives greyscale and a still
      // frame in a paused replay.
      ctx.save()
      ctx.strokeStyle = TRIM
      ctx.lineWidth = lw * 1.2
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.beginPath()
      ctx.moveTo(x + s * 0.36, y + s * 0.17)
      ctx.lineTo(x + s * 0.7, y + s * 0.17)
      ctx.moveTo(x + s * 0.6, y + s * 0.1)
      ctx.lineTo(x + s * 0.7, y + s * 0.17)
      ctx.lineTo(x + s * 0.6, y + s * 0.24)
      ctx.stroke()
      ctx.restore()
      break
    }
    case 'plate_return': {
      // The washing-up chute: a slide dropping in from the top right, and the
      // dirty plates piling up under its mouth.
      //
      // It was the pass's belt run backwards, which made the two stations one
      // object with two arrows — see the hatch above. A chute is the opposite
      // kind of thing to a window: no frame, no sill, a slope instead of a
      // horizontal, and the bottom-centre of the tile left clear because
      // `stations.ts` draws the actual pile there from `dirtyPlates`. The
      // fixture is the empty chute; the state is the heap under it.
      //
      // Nothing here moves. The pass moves because a pass is a machine with
      // service running through it; a chute is a piece of bent steel, and the
      // asymmetry is one more thing telling the two apart out of focus.
      ctx.beginPath()
      ctx.moveTo(x + s * 0.99, y + s * 0.04)
      ctx.lineTo(x + s * 0.3, y + s * 0.42)
      ctx.lineTo(x + s * 0.3, y + s * 0.62)
      ctx.lineTo(x + s * 0.99, y + s * 0.24)
      ctx.closePath()
      solid(ctx, '#7b8794', '#23282e', lw)

      // The trough's inside face, so the chute is a channel rather than a
      // plank: one darker band along its upper edge.
      ctx.save()
      ctx.beginPath()
      ctx.moveTo(x + s * 0.99, y + s * 0.04)
      ctx.lineTo(x + s * 0.3, y + s * 0.42)
      ctx.lineTo(x + s * 0.3, y + s * 0.52)
      ctx.lineTo(x + s * 0.99, y + s * 0.14)
      ctx.closePath()
      ctx.fillStyle = '#3d444d'
      ctx.fill()
      ctx.restore()

      // The mouth: a lip across the low end, where a plate drops out.
      ctx.beginPath()
      roundRect(ctx, x + s * 0.2, y + s * 0.38, s * 0.12, s * 0.3, s * 0.04)
      solid(ctx, '#8d99a6', '#3d444d', lw * 0.7)

      // A bracket holding the high end onto the wall, so it hangs rather than
      // floats.
      ctx.beginPath()
      roundRect(ctx, x + s * 0.82, y + s * 0.16, s * 0.1, s * 0.22, s * 0.03)
      solid(ctx, '#39404a', '#1c2025', lw * 0.6)
      break
    }
    case 'plate_stack': {
      // The shelf only. The stack itself is state — how many plates are left —
      // and `stations.ts` draws it, so it is not drawn twice.
      ctx.beginPath()
      roundRect(ctx, x + s * 0.14, y + s * 0.74, s * 0.72, s * 0.12, s * 0.05)
      solid(ctx, '#8d99a6', '#4a525b', lw * 0.7)
      ctx.beginPath()
      roundRect(ctx, x + s * 0.2, y + s * 0.86, s * 0.6, s * 0.06, s * 0.03)
      solid(ctx, '#5c6570', '#33393f', lw * 0.5)
      break
    }
    case 'extinguisher_rack': {
      // A wall point, painted red, with two steel brackets. What hangs in them
      // is drawn by `stations.ts` from state, because the rack can be empty —
      // and an empty rack has to be readable as "the extinguisher is not here",
      // which is why the painted panel and the brackets stay behind it.
      ctx.beginPath()
      roundRect(ctx, x + s * 0.14, y + s * 0.12, s * 0.72, s * 0.76, s * 0.07)
      solid(ctx, '#8f2318', '#3d0f09', lw)
      ctx.beginPath()
      roundRect(ctx, x + s * 0.2, y + s * 0.18, s * 0.6, s * 0.56, s * 0.05)
      ctx.fillStyle = 'rgba(255, 226, 200, 0.1)'
      ctx.fill()
      // A ghost of what belongs here, so an empty rack says "the extinguisher
      // is not in it" rather than "this is a red box".
      ctx.save()
      // Exactly where `stations.ts` hangs the real one, so a racked
      // extinguisher covers its ghost instead of doubling up beside it.
      ctx.globalAlpha = 0.4
      drawExtinguisher(
        ctx,
        x + s * RACKED_EXTINGUISHER.x,
        y + s * RACKED_EXTINGUISHER.y,
        s * RACKED_EXTINGUISHER.r,
      )
      ctx.restore()
      for (const t of [0.34, 0.66]) {
        ctx.beginPath()
        roundRect(ctx, x + s * 0.22, y + s * t, s * 0.56, s * 0.07, s * 0.035)
        solid(ctx, '#b3bcc6', '#4a525b', lw * 0.6)
      }
      // A hazard strip along the foot: "do not park anything here".
      ctx.save()
      ctx.beginPath()
      ctx.rect(x + s * 0.14, y + s * 0.78, s * 0.72, s * 0.1)
      ctx.clip()
      ctx.fillStyle = '#1a0f07'
      ctx.fillRect(x + s * 0.14, y + s * 0.78, s * 0.72, s * 0.1)
      ctx.strokeStyle = TRIM
      ctx.lineWidth = s * 0.05
      for (let i = -1; i < 8; i += 1) {
        ctx.beginPath()
        ctx.moveTo(x + s * (0.1 + i * 0.12), y + s * 0.9)
        ctx.lineTo(x + s * (0.18 + i * 0.12), y + s * 0.76)
        ctx.stroke()
      }
      ctx.restore()
      break
    }
    case 'crate': {
      // Slatted box. The ingredient it dispenses is drawn on top of it by
      // `stations.ts` — the crate IS its contents, as in the reference.
      ctx.beginPath()
      roundRect(ctx, x + s * 0.1, y + s * 0.24, s * 0.8, s * 0.64, s * 0.06)
      solid(ctx, '#9c6f43', '#5f4227', lw)
      ctx.strokeStyle = '#7c552f'
      ctx.lineWidth = lw * 0.9
      for (const t of [0.44, 0.62, 0.8]) {
        ctx.beginPath()
        ctx.moveTo(x + s * 0.12, y + s * t)
        ctx.lineTo(x + s * 0.88, y + s * t)
        ctx.stroke()
      }
      // Corner posts, so the box has depth rather than being a striped square.
      ctx.fillStyle = '#7c552f'
      for (const t of [0.1, 0.83]) {
        ctx.fillRect(x + s * t, y + s * 0.24, s * 0.07, s * 0.64)
      }
      break
    }
    default:
      break
  }
  ctx.restore()
}

/**
 * A station drawn small, for a ticket card.
 *
 * Not the same drawing as the tile. A tile fixture is a dark object on a lit
 * bench and reads by contrast against it; the same paths on cream paper at 26
 * pixels collapse into a black square, which is what the first pass of this
 * phase actually produced. So the badge versions are open pictograms — outlines
 * and a couple of marks — that read on paper.
 *
 * The pictograms themselves live in `badgeShapes.ts`, one per `StationType` in
 * a `Record` that cannot have a hole in it. This function is only the pen: it
 * sets ticket ink, a stroke weight scaled to the badge, round caps and joins,
 * and hands the path the geometry. A station type outside the contract — a
 * stale client talking to a newer server — falls to `badgeFallback`.
 */
export function drawStationBadge(
  ctx: CanvasRenderingContext2D,
  type: StationType,
  cx: number,
  cy: number,
  r: number,
): void {
  const lw = Math.max(1.4, r * 0.17)
  ctx.save()
  ctx.strokeStyle = BADGE_INK
  // Filled where a mark is a dot rather than an outline — a board's hanging
  // hole, a bell's knob — so those never inherit whatever the caller left set.
  ctx.fillStyle = BADGE_INK
  ctx.lineWidth = lw
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  const path = BADGE_PATHS[type] ?? badgeFallback
  path(ctx, cx, cy, r, lw)
  ctx.restore()
}
